import { Skeleton, Splitter, Tabs } from 'antd'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { textSubmissionDrawerActions, ViewModes } from '../../../../../../../store/slices/text-submission-drawer.slice'
import textSubmissionDrawerSelectors from '../../../../../../../store/selectors/text-submission-drawer.selectors'
import GradingSection from './sections/grading/GradingSection'
import AnswerEditor from './AnswerEditor'
import { AnswerContextProvider } from './AnswerContext'
import GuidelinesSection from './sections/guidelines/GuidelinesSection'
import HistorySection from './sections/history/HistorySection'
import SimilarAnswersSection from './sections/similar-answers/SimilarAnswersSection'
import useLocalStorage from '../../../../../../../hooks/useLocalStorage'
import { LocalStorageKeys } from '../../../../../../../constants'
import Clarity from '@microsoft/clarity'

export const AnswerTabs = {
    GRADING: 'grading',
    INSTRUCTIONS: 'instructions',
    SIMILAR_ANSWERS: 'similarAnswers',
    HISTORY: 'history',
}

// Default splitter ratio to avoid creating new array on every render
const DEFAULT_SPLITTER_RATIO = ['50%', '50%']

const Answer = ({ submissionId, answers, loading, question }) => {
    const dispatch = useDispatch()
    const viewMode = useSelector(textSubmissionDrawerSelectors.viewMode)
    
    const [splitterRatio, setSplitterRatio] = useLocalStorage(LocalStorageKeys.ANSWER_SPLITTER_RATIO, DEFAULT_SPLITTER_RATIO)

    const items = [
        {
            key: AnswerTabs.GRADING,
            label: 'Grading',
            children: <GradingSection answers={answers} question={question} />,
        },
        {
            key: AnswerTabs.INSTRUCTIONS,
            label: 'Guidelines',
            children: <GuidelinesSection question={question} answers={answers} />,
        },
        {
            key: AnswerTabs.SIMILAR_ANSWERS,
            label: 'Similar Answers',
            children: <SimilarAnswersSection question={question} answers={answers} />,
        },
        {
            key: AnswerTabs.HISTORY,
            label: 'History',
            children: <HistorySection question={question} answers={answers} />,
        },
    ]

    const [alreadyResized, setAlreadyResized] = useState(false)
    const handleSplitterResize = (sizes) => {
        setSplitterRatio(sizes)

        if (!alreadyResized) {
            Clarity.setTag('answerSplitterRatio')
            setAlreadyResized(true)
        }
    }

    if (loading) return <Skeleton active />

    const hasRubrics = question.questionRubrics?.length > 0

    // Question has no rubrics - show only the answer editor
    if (!hasRubrics) {
        return (
            <AnswerContextProvider answers={answers} question={question}>
                <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                    <AnswerEditor 
                        submissionId={submissionId} 
                        answers={answers} 
                        question={question} 
                        maxHeight="calc(100vh - 360px)"
                    />
                </div>
            </AnswerContextProvider>
        )
    }
    
    // For PDF mode, show full width grading panel
    if (viewMode === ViewModes.PDF) {
        return (
            <AnswerContextProvider answers={answers} question={question}>
                <div style={{ width: '100%' }}>
                    <Tabs
                        onChange={key => dispatch(textSubmissionDrawerActions.setActiveSectionKey(key))}
                        size='small'
                        activeKeys={[AnswerTabs.GRADING]}
                        items={items}
                        rootClassName='section-tabs'
                    />
                </div>
            </AnswerContextProvider>
        )
    }

    return (
        <AnswerContextProvider answers={answers} question={question}>
            <div>
                <Splitter
                    onResize={handleSplitterResize}
                    style={{ height: '100%' }}
                >
                    <Splitter.Panel size={splitterRatio[0]} minSize="20%" style={{ paddingRight: 8, overflow: 'visible' }}>
                        <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ position: 'sticky', top: 0, zIndex: 2, background: 'transparent' }}>
                                <AnswerEditor 
                                    submissionId={submissionId} 
                                    answers={answers} 
                                    question={question} 
                                    maxHeight="calc(100vh - 360px)"
                                />
                            </div>
                        </div>
                    </Splitter.Panel>
                    <Splitter.Panel size={splitterRatio[1]} minSize="20%" style={{ paddingLeft: 8 }}>
                        <Tabs
                            onChange={key => dispatch(textSubmissionDrawerActions.setActiveSectionKey(key))}
                            size='small'
                            activeKeys={[AnswerTabs.GRADING]}
                            items={items}
                            rootClassName='section-tabs'
                        />
                    </Splitter.Panel>
                </Splitter>
            </div>
        </AnswerContextProvider>
    )
}

export default Answer
